<template>
  <vs-list class="flex flex-wrap">
    <div
      :class="wide ? 'w-1/2' : 'w-full'"
      v-for="content in filteredContentTypes"
      :key="`related-${content.title}`"
    >
      <vs-list-header
        icon="icon-git-pull-request"
        iconPack="feather"
        :title="content.title"
      ></vs-list-header>
      <div v-if="isLoading" class="loading-state">
        <div class="spinner"></div>
        <span>Loading...</span>
      </div>
      <template v-else-if="content.items.length">
        <vs-list-item
          v-for="item in content.items"
          :key="item.id"
          :icon="content.icon"
          iconPack="feather"
          :title="item.name"
          :subtitle="item.modified | dateFromFormat"
        >
          <vs-chip v-for="la in item.learning_areas" :key="la.id" transparent>
            {{ getLearningAreaName(la) }}
          </vs-chip>
        </vs-list-item>
      </template>
      <div v-else class="empty-state">
        No {{ content.title.toLowerCase() }} available.
      </div>
    </div>
  </vs-list>
</template>

<script>
import { mapActions } from 'vuex'
import { getBasicData } from '@/utils/mixins.js'

export default {
  name: 'RelatedContentList',
  mixins: [getBasicData],
  props: {
    metadata: {
      type: Object,
      required: false, // Make it optional
      default: () => ({
        courses: [],
        learning_paths: [],
        collections: [],
      }), // Provide a default value
    },
    wide: {
      type: Boolean,
      default: false,
    },
    allowedParents: {
      type: Array,
      default: () => ['courses', 'learning_paths', 'collections'],
    },
  },
  data() {
    return {
      courses: [],
      learningPaths: [],
      collections: [],
      isLoading: null,
    }
  },
  computed: {
    contentTypes() {
      return [
        {
          key: 'courses',
          title: 'Courses',
          items: this.courses,
          icon: 'icon-book',
        },
        {
          key: 'learning_paths',
          title: 'Learning Paths',
          items: this.learningPaths,
          icon: 'icon-git-commit',
        },
        {
          key: 'collections',
          title: 'Collections',
          items: this.collections,
          icon: 'icon-shopping-bag',
        },
      ]
    },
    filteredContentTypes() {
      return this.contentTypes.filter((content) =>
        this.allowedParents.includes(content.key)
      )
    },
  },
  async created() {
    this.fetchContent()
  },
  methods: {
    ...mapActions('collections', ['searchCollections']),
    ...mapActions('courses', ['searchCourses']),
    ...mapActions('lpaths', ['searchLPaths']),
    async fetchContent() {
      this.isLoading = true
      try {
        const metadata = this.metadata || {
          courses: [],
          learning_paths: [],
          collections: [],
        }

        const {
          courses: courseIds = [],
          learning_paths: learningPathIds = [],
          collections: collectionIds = [],
        } = metadata

        if (
          !Array.isArray(courseIds) ||
          !Array.isArray(learningPathIds) ||
          !Array.isArray(collectionIds)
        ) {
          this.isLoading = false
          return
        }

        const [coursesResponse, learningPathsResponse, collectionsResponse] =
          await Promise.all([
            courseIds.length
              ? this.searchCourses({ ids: courseIds })
              : Promise.resolve({ results: [] }),
            learningPathIds.length
              ? this.searchLPaths({ ids: learningPathIds })
              : Promise.resolve({ results: [] }),
            collectionIds.length
              ? this.searchCollections({ ids: collectionIds })
              : Promise.resolve({ results: [] }),
          ])

        this.courses = coursesResponse.results
        this.learningPaths = learningPathsResponse.results
        this.collections = collectionsResponse.results
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },
    getLearningAreaName(id) {
      return this.getLAreasEntries[id] ? this.getLAreasEntries[id].name : ''
    },
  },
}
</script>

<style scoped>
.empty-state {
  padding: 10px;
  text-align: center;
  color: #999;
}
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100px;
}
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
